import { useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ChatDrawer } from './ChatDrawer';
import { ChatMobile } from './ChatMobile';
import { NotificationPermissionBanner } from '../notifications/NotificationPermissionBanner';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../context/SocketContext';
import { LayoutSkeleton } from '../ui/LayoutSkeleton';
import { StaggerGroup } from '../ui/StaggerIn';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';

export function Layout() {
  const { isAuthenticated, isLoading } = useAuth();
  const { activeConversationId, chatExpanded } = useSocket();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useSwipeNavigation<HTMLElement>({ enabled: false });

  if (isLoading) {
    return <LayoutSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const chatOpen = !!activeConversationId;

  const getContentPadding = () => {
    if (!chatOpen) return 'lg:pl-64';
    if (chatExpanded) return 'lg:pl-[calc(100vw-20%)]';
    return 'lg:pl-[736px]';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Chat drawer — slides in to the right of the sidebar (desktop) or full-screen (mobile) */}
      <ChatDrawer isOpen={chatOpen} />

      {/* Mobile chat FAB + bottom sheet — hidden on desktop */}
      <ChatMobile />

      {/* Main content shifts right when chat is open */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${getContentPadding()}`}
        data-chat-expanded={chatOpen && chatExpanded ? 'true' : 'false'}
      >
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main ref={mainRef} className="flex-1 p-4 md:p-6 lg:p-8">
          <NotificationPermissionBanner />
          <StaggerGroup>
            <Outlet />
          </StaggerGroup>
        </main>

        {/* Footer */}
        <footer className="border-t border-border bg-surface px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
            <div className="text-xs text-muted">
              BandMate - Collaborative Music Production
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
